// v2 tortoise — builds the signature shell properly: a high dome studded with a
// crown scute and a ring of raised scute plates over a flared angular rim skirt
// and a pale plastron underneath, plus a wrinkled out-stretched neck, a beaked
// head, stumpy elephant-column legs and a stub tail. Two liveries: olive-brown
// desert tortoise and a mossy green one. Ported 1:1 from age-of-mcp's
// v2/tortoise.ts, using the library's shared `wildland` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { dome: "#6a5832", scute: "#88743e", rim: "#92804c", skin: "#9d8c60",
    dark: "#6e6040", plastron: "#c4b186" },
  { dome: "#5c5e30", scute: "#787a3c", rim: "#84864a", skin: "#92906a",
    dark: "#5e6040", plastron: "#bcb988" },
];

function tortoiseParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // domed shell over a flared six-sided rim skirt and pale plastron
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.dome,
      position: [0, 0.031, 0], scale: [1.35, 0.92, 1.2] },
    { geometry: new THREE.CylinderGeometry(0.036, 0.04, 0.012, 6), color: c.rim,
      position: [0, 0.024, 0], scale: [1.1, 1, 1.25] },
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.plastron,
      position: [0, 0.016, 0], scale: [1.5, 0.45, 1.2] },
    // crown scute + ring of raised scutes (the shell pattern)
    { geometry: new THREE.IcosahedronGeometry(0.0075, 0), color: c.scute,
      position: [0, 0.0545, 0], scale: [1, 0.45, 1] },
    // wrinkled neck reaching out, beaked head
    wildland.tube([0.034, 0.024, 0], [0.052, 0.034, 0], 0.0075, 0.006, c.skin),
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.skin,
      position: [0.058, 0.038, 0], scale: [1.35, 0.95, 0.9] },
    { geometry: new THREE.ConeGeometry(0.0045, 0.009, 4), color: c.dark,
      position: [0.071, 0.035, 0], rotation: [0, 0, -Math.PI / 2] },
    // stub tail
    { geometry: new THREE.ConeGeometry(0.004, 0.012, 4), color: c.skin,
      position: [-0.04, 0.018, 0], rotation: [0, 0, 1.9] },
  ];
  for (const [sx, sz] of [[0.018, 0.014], [0.018, -0.014],
    [-0.016, 0.014], [-0.016, -0.014]] as Array<[number, number]>) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.0075, 0),
      color: c.scute, position: [sx, 0.048, sz], scale: [1, 0.5, 1] });
  }
  // stumpy elephant-column legs (flared at the foot)
  for (const [lx, lz] of [[0.022, 0.026], [0.022, -0.026],
    [-0.022, 0.026], [-0.022, -0.026]] as Array<[number, number]>) {
    parts.push({ geometry: new THREE.CylinderGeometry(0.0075, 0.0085, 0.03, 5),
      color: c.dark, position: [lx, 0.015, lz] });
  }
  parts.push(...wildland.eyes(0.063, 0.042, 0.0075, 0.0028, "#16130e"));
  return parts;
}

export const tortoiseV2 = defineModel({
  id: "tortoise-v2",
  batchId: "fable",
  name: "Tortoise (v2)",
  tags: ["creature","tortoise","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tortoise.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => tortoiseParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => tortoiseParts(1) },
  ],
  animations: [],
});
